import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { OperationsUser, OperationsUserProfile, OperationsRole } from '@/models/user.model';
import {
  auditLoginSuccess,
  auditLoginFailure,
  auditLogout,
  initializeAuditSession,
  terminateAuditSession,
} from '@/utils/audit-integration';

export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signIn(email: string, password: string): Promise<OperationsUserProfile> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const profile = await this.getUserProfile(userCredential.user.uid);
      
      // Initialize audit session
      initializeAuditSession();
      
      // Log successful login
      auditLoginSuccess(
        userCredential.user.uid,
        email,
        profile.fullName
      ).catch(console.error);
      
      // Update last login timestamp
      try {
        await updateDoc(doc(db, 'operationsUsers', userCredential.user.uid), {
          lastLogin: Timestamp.now(),
        });
      } catch (e) {
        console.error('Failed to update lastLogin:', e);
      }
      
      return profile;
    } catch (error) {
      // Log failed login attempt
      auditLoginFailure(
        email,
        error instanceof Error ? error.message : 'Unknown error'
      ).catch(console.error);
      
      throw error;
    }
  }

  /**
   * Sign out current user
   */
  static async signOut(): Promise<void> {
    const user = auth.currentUser;
    
    if (user) {
      // Log logout before signing out
      auditLogout(user.uid, user.email || 'unknown').catch(console.error);
      
      // Clear audit session
      terminateAuditSession();
    }
    
    await signOut(auth);
  }

  /**
   * Get current user
   */
  static getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Get user profile from Firestore
   */
  static async getUserProfile(userId: string): Promise<OperationsUserProfile> {
    const userDoc = await getDoc(doc(db, 'operationsUsers', userId));
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found. Access denied.');
    }

    const data = userDoc.data();
    const user: OperationsUser = {
      id: userDoc.id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role as OperationsRole,
      status: data.status,
      department: data.department,
      phoneNumber: data.phoneNumber,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLogin: data.lastLogin?.toDate(),
      metadata: data.metadata,
    };

    return {
      ...user,
      fullName: `${user.firstName} ${user.lastName}`,
      isActive: user.status === 'active',
      canAccessSales: this.canAccessSales(user.role),
      canAccessHR: this.canAccessHR(user.role),
      canAccessLegal: this.canAccessLegal(user.role),
      canAccessCompliance: this.canAccessCompliance(user.role),
    };
  }

  /**
   * Check if user can access sales features
   */
  static canAccessSales(role: OperationsRole | string): boolean {
    const roleStr = typeof role === 'string' ? role : role;
    return [
      OperationsRole.sales,
      OperationsRole.salesManager,
      OperationsRole.operationsAdmin,
      OperationsRole.executive,
      'sales',
      'sales_manager',
      'salesManager',
      'operations_admin',
      'operationsAdmin',
      'executive',
    ].includes(roleStr as any);
  }

  /**
   * Check if user can access HR features
   */
  static canAccessHR(role: OperationsRole | string): boolean {
    const roleStr = typeof role === 'string' ? role : role;
    return [
      OperationsRole.hr,
      OperationsRole.hrManager,
      OperationsRole.operationsAdmin,
      OperationsRole.executive,
      'hr',
      'hr_manager',
      'hrManager',
      'operations_admin',
      'operationsAdmin',
      'executive',
    ].includes(roleStr as any);
  }

  /**
   * Check if user can access legal features
   */
  static canAccessLegal(role: OperationsRole | string): boolean {
    const roleStr = typeof role === 'string' ? role : role;
    return [
      OperationsRole.legal,
      OperationsRole.operationsAdmin,
      OperationsRole.executive,
      'legal',
      'operations_admin',
      'operationsAdmin',
      'executive',
    ].includes(roleStr as any);
  }

  /**
   * Check if user can access compliance features
   */
  static canAccessCompliance(role: OperationsRole | string): boolean {
    const roleStr = typeof role === 'string' ? role : role;
    return [
      OperationsRole.compliance,
      OperationsRole.operationsAdmin,
      OperationsRole.executive,
      'compliance',
      'operations_admin',
      'operationsAdmin',
      'executive',
    ].includes(roleStr as any);
  }

  /**
   * Subscribe to auth state changes
   */
  static onAuthStateChange(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  }
}

